import type { TFunction } from 'i18next';
import { ThroughputType } from '../../../../../../store/strategy-store/strategy-store.type';
import { graphCambridgeBlue, redBase, secondaryBase, skyDark } from '../../../../../../styles/design-tokens';
import { Metric } from '../../../../strategy.type';
import { type ChartData, DataKey, type LegendItem } from './milestone-progress-chart.type';

/**
 * Get the color for a specific dataKey in the milestone progress chart
 *
 * @param {string} dataKey - The dataKey of the chart item
 * @param {string} [defaultColor] - The default color to use if no specific color is assigned
 * @returns {string} - The color to use for the chart item
 */
const getItemColor = (dataKey: string, defaultColor?: string): string => {
  switch (dataKey) {
    case 'total':
      return secondaryBase;
    case 'completed':
      return graphCambridgeBlue;
    case 'projection':
      return skyDark;
    default:
      return defaultColor || skyDark;
  }
};

/**
 * Gets the display name for a chart line based on the data key and metric type
 *
 * @param {DataKey} dataKey - The data key identifying which line in the chart (completed/total, regular/future)
 * @param {Metric} metric - The metric type being displayed (tasks or points)
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The translated display name for the chart line
 */
const getChartLineName = (dataKey: DataKey, metric: Metric, t: TFunction): string => {
  const work_units = t('common.work_units');
  const estimation = t('common.estimation');

  switch (dataKey) {
    case DataKey.CompletedRegular:
      return metric === Metric.Tasks
        ? getChartLineNameT('completed_tasks', { work_units }, t)
        : getChartLineNameT('completed_points', { estimation }, t);
    case DataKey.TotalRegular:
      return metric === Metric.Tasks
        ? getChartLineNameT('total_tasks', { work_units }, t)
        : getChartLineNameT('total_points', { estimation }, t);
    case DataKey.CompletedFuture:
      return metric === Metric.Tasks
        ? getChartLineNameT('future_completed_tasks', { work_units }, t)
        : getChartLineNameT('future_completed_points', { estimation }, t);
    case DataKey.TotalFuture:
      return metric === Metric.Tasks
        ? getChartLineNameT('future_total_tasks', { work_units }, t)
        : getChartLineNameT('future_total_points', { estimation }, t);
    default:
      return t('strategy.initiatives.labels.no_data');
  }
};

// Narrow union of the 8 chart-label suffixes this helper resolves. Keeps the
// template-literal key `strategy.initiative_performance.chart_labels.${key}`
// provably present in the dictionary (all 8 entries exist under
// chart_labels) — no runtime cast or `as never` escape hatch needed.
type ChartLabelKey =
  | 'completed_tasks'
  | 'completed_points'
  | 'total_tasks'
  | 'total_points'
  | 'future_completed_tasks'
  | 'future_completed_points'
  | 'future_total_tasks'
  | 'future_total_points';

/**
 * Helper function to get translated chart line names.
 *
 * The base dictionary uses `$t(common.work_units)` / `$t(common.estimation)`
 * cross-references, so `params` is ignored by i18next's default resolution.
 * We still pass the params as a fallback for customer `text_config` overrides
 * that replace the template with a `{{work_units}}`-style interpolation — in
 * that path the params keep the label from rendering as `"Total {{work_units}}"`.
 *
 * @param {ChartLabelKey} key - The chart-label suffix identifying which line
 *   name to translate. Constrained to the 8 suffixes that exist under
 *   `strategy.initiative_performance.chart_labels` in the base dictionary.
 * @param {Record<string, string>} params - Interpolation fallback for customer
 *   overrides that use `{{var}}` templates instead of `$t()` cross-references.
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The translated text for the chart line name.
 */
const getChartLineNameT = (key: ChartLabelKey, params: Record<string, string>, t: TFunction): string => {
  // Single-cast `t` against its plain (key, options) signature to dodge an
  // i18next v26 overload-resolution quirk: with a narrow literal key union,
  // the typed `t` selects the `(key, defaultValue, options)` overload and
  // rejects the Record in the 2nd position. Casting the function reference
  // picks the plain overload; the `ChartLabelKey` union above still guards
  // every call site, and all 8 dictionary values are strings so the `string`
  // return is safe.
  const tSimple = t as (k: string, opts?: Record<string, unknown>) => string;
  return tSimple(`strategy.initiative_performance.chart_labels.${key}`, params);
};

/**
 * Gets the legend items configuration for the milestone progress chart
 *
 * @returns {LegendItem[]} An array of legend items containing data keys, throughput types, and colors
 */
const getLegendItems = (): LegendItem[] => {
  return [
    {
      dataKey: DataKey.TotalRegular,
      throughputType: ThroughputType.Total,
      color: secondaryBase,
    },
    {
      dataKey: DataKey.CompletedRegular,
      throughputType: ThroughputType.Completed,
      color: graphCambridgeBlue,
    },
  ];
};

/**
 * Gets milestone reference line items for the legend
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Array} An array of milestone reference line items with label and color
 */
const getMilestoneReferenceLineItems = (t: TFunction) => {
  return [
    {
      label: t('strategy.initiative_performance.chart_labels.target_end_date', 'Target End Date'),
      color: redBase,
    },
  ];
};

/**
 * Determines if the tooltip is showing a transitional period between regular and future data
 *
 * @param {TooltipProps<number, string>['payload']} payload - The tooltip payload containing data points
 * @returns {boolean} True if the tooltip shows both regular and future data for either completed or total metrics
 */
const isTransitionalTooltip = (payload: any[]): boolean => {
  if (!payload) {
    return false;
  }

  const payloadKeys = payload.map((entry: any) => entry.dataKey);
  const hasCompletedKeys = [DataKey.CompletedRegular, DataKey.CompletedFuture].every((key) =>
    payloadKeys.includes(key),
  );
  const hasTotalKeys = [DataKey.TotalRegular, DataKey.TotalFuture].every((key) => payloadKeys.includes(key));

  return hasCompletedKeys || hasTotalKeys;
};

/**
 * Formats the milestone progress data for flora
 *
 * @param {ChartData} chartData - The chart data
 * @param {Metric} metric - The metric
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<string, Record<string, number | null>>} The formatted data
 */
const formatMilestoneProgressDataForFlora = (
  chartData: ChartData,
  metric: Metric,
  t: TFunction,
): Record<string, Record<string, number | null>> => {
  const formattedData: Record<string, Record<string, number | null>> = {};

  const metricKey =
    metric === Metric.Tasks ? t('common.work_units').toLowerCase() : t('common.estimation').toLowerCase();
  chartData.forEach((data) => {
    formattedData[data.rawDate] = {
      [`completed_${metricKey}`]: data[DataKey.CompletedRegular],
      [`total_${metricKey}`]: data[DataKey.TotalRegular],
      [`future_completed_${metricKey}`]: data[DataKey.CompletedFuture],
      [`future_total_${metricKey}`]: data[DataKey.TotalFuture],
    };
  });

  return formattedData;
};

export {
  formatMilestoneProgressDataForFlora,
  getChartLineName,
  getChartLineNameT,
  getItemColor,
  getLegendItems,
  getMilestoneReferenceLineItems,
  isTransitionalTooltip,
};
